<template>
  <div>
    <div class="d-flex align-center justify-space-between mb-2">
      <p class="text-subtitle-2" data-testid="banner-preview-heading">
        Customer preview on {{ desktop ? "desktop" : "mobile" }}
      </p>
      <SoToggle
        v-model="desktop"
        label="Desktop"
        aria-label="Show the desktop preview"
        data-testid="banner-preview-desktop"
      />
    </div>
    <div
      class="banner"
      :class="{ 'banner--desktop': desktop }"
      data-testid="banner-preview"
    >
      <div class="banner-copy">
        <p class="banner-title" data-testid="banner-preview-title">
          {{ copy.title }}
        </p>
        <p class="banner-content" data-testid="banner-preview-content">
          {{ copy.subtitle }}
        </p>
        <span
          v-if="copy.buttonText"
          class="banner-button"
          data-testid="banner-preview-button"
        >
          {{ copy.buttonText }}
        </span>
      </div>
      <img
        v-if="illustrationUrl"
        class="banner-illustration"
        :src="illustrationUrl"
        alt=""
        data-testid="banner-preview-illustration"
      />
    </div>
  </div>
</template>

<script lang="ts">
  import { defineComponent, PropType } from "vue"
  import SoToggle from "@/components/formControls/SoToggle.vue"
  import { ILLUSTRATION_URLS } from "@/views/admin/marketingBanners/marketingBannerIllustrations"
  import { IBannerCopy } from "@/views/admin/marketingBanners/marketingBannerCopy"

  export default defineComponent({
    components: { SoToggle },
    props: {
      copy: {
        type: Object as PropType<IBannerCopy>,
        required: true,
      },
    },
    data() {
      return { desktop: true }
    },
    computed: {
      illustrationUrl(): string | undefined {
        return ILLUSTRATION_URLS[this.copy.illustrationName]
      },
    },
  })
</script>

<!-- Copies fe-nexus Banner.vue at its width on a 375px phone and on the desktop home page; customer palette, not Nova's theme. -->
<style scoped>
  .banner {
    --customer-lime: hsl(71 100% 49%);
    --customer-dark: hsl(153 7% 24%);
    --customer-ink: hsl(60 33% 6%);
    display: flex;
    flex-wrap: wrap;
    background-color: var(--customer-dark);
    border: 1px solid var(--customer-lime);
    border-radius: 12px;
    padding: 16px;
    color: white;
    font-family: "Founders Grotesk", sans-serif;
  }
  .banner:not(.banner--desktop) {
    width: 327px;
  }
  .banner-copy {
    display: contents;
  }
  .banner-title {
    flex-basis: 100%;
    font-size: 1.5rem;
    line-height: 2rem;
    font-weight: 500;
    letter-spacing: -0.00375rem;
    margin-bottom: 4px;
  }
  .banner-content {
    flex: 1;
    font-size: 1rem;
    line-height: 1.5rem;
    margin-bottom: 16px;
  }
  .banner-illustration {
    height: 92px;
    margin-left: 8px;
  }
  .banner-button {
    order: 1;
    flex-basis: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 36px;
    margin-top: 8px;
    padding: 8px 24px;
    border-radius: 360px;
    background-color: var(--customer-lime);
    color: var(--customer-ink);
    font-family: Arial, sans-serif;
    font-size: 0.875rem;
    line-height: 1.25rem;
    white-space: nowrap;
  }
  .banner--desktop {
    width: 745px;
    zoom: 0.77;
    flex-wrap: nowrap;
    padding: 32px;
  }
  .banner--desktop .banner-copy {
    display: block;
    flex: 1;
  }
  .banner--desktop .banner-title {
    font-size: 2.5rem;
    line-height: 2.5rem;
    letter-spacing: -0.025rem;
    margin-bottom: 8px;
  }
  .banner--desktop .banner-content {
    font-size: 1.125rem;
    line-height: 1.75rem;
    margin-bottom: 24px;
  }
  .banner--desktop .banner-illustration {
    height: 140px;
    margin: 0 16px 0 24px;
  }
  .banner--desktop .banner-button {
    display: inline-flex;
    height: 40px;
    margin-top: 0;
    font-size: 1rem;
    line-height: 1.5rem;
  }
</style>
